import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './LocationManagement.module.css';
import { locationApi, Country } from '../../services/locationApi';

const LocationManagement: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const data = await locationApi.getAllCountries();
        
        if (!Array.isArray(data)) {
          console.error('Invalid response format:', data);
          toast.error('Invalid response format from server', {
            position: "top-right",
            autoClose: 5000
          });
          return;
        }
        
        setCountries(data);
        setFilteredCountries(data);
      } catch (error: any) {
        console.error('Error fetching countries:', error);
        const errorMessage = error?.response?.data?.detail || 'Failed to load countries. Please try again later.';
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  // Filter countries when search term changes
  useEffect(() => {
    if (searchTerm) {
      const filtered = countries.filter(country =>
        (country.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (country.abbrev || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries);
    }
  }, [searchTerm, countries]);

  const handleDeleteCountry = (countryId: string) => {
    toast.dismiss(); // Clear any existing toasts
    
  const country = countries.find(c => c.Country_ID === countryId);
  const countryName = country ? country.name : `ID ${countryId}`;
    
    toast(
      <div>
        <p>Are you sure you want to delete the country "{countryName}"?</p>
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={async () => {
              toast.dismiss();
              try {
                await locationApi.deleteCountry(countryId);
                // Remove the country from state
                setCountries(prevCountries => prevCountries.filter(c => c.Country_ID !== countryId));
                toast.success('Country deleted successfully', {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              } catch (error: any) {
                console.error('Error deleting country:', error);
                const errorMessage = error?.response?.data?.detail || 'Failed to delete country. Please try again.';
                toast.error(errorMessage, {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  const navigateToCountry = (countryId: string) => {
    navigate(`/admin/locations/${countryId}`);
  };

  return (
    <div className="container mt-4">
      {/* Toast Container with custom settings */}
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
          </li>
          <li className="breadcrumb-item active">Country Management</li>
        </ol>
      </nav>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className={styles.searchContainer}>
          <input 
            id="search-input" 
            type="text" 
            className={`form-control ${styles.searchInput}`} 
            placeholder="Search location..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Link className="btn btn-primary" to="/admin/locations/create">
          Create new Country
        </Link>
      </div>

      <div id="location-list-container" className={styles.locationListContainer}>
        <div className={`${styles.mainBox} ${styles.clearfix}`}>
          <div className="table-responsive">
            <table className={`table ${styles.locationTable}`}>
              <thead>
                <tr>
                  <th className={styles.locationTableHeader}>Country</th>
                  <th className={styles.locationTableHeader}>Abbrev</th>
                  <th className={styles.locationTableHeader}>City Count</th>
                  <th className={styles.locationTableHeader}>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="text-center p-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">Loading countries...</div>
                    </td>
                  </tr>
                ) : filteredCountries.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center">
                      {countries.length > 0 ? 
                        'No countries match your search criteria' : 
                        'No countries found. Please check API connection.'}
                    </td>
                  </tr>
                ) : (
                  filteredCountries.map(country => (
                    <tr key={country.Country_ID}>
                      <td className={styles.locationTableCell}>
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault();
                            navigateToCountry(country.Country_ID);
                          }} 
                          className="text-decoration-none fw-semibold"
                        >
                          {country.name}
                        </a>
                      </td>
                      <td className={styles.locationTableCell}>
                        {country.abbrev}
                      </td>
                      <td className={styles.locationTableCell}>
                        {country.city_count || 0}
                      </td>
                      <td className={`text-end ${styles.locationTableCell}`}>
                        <div className={`btn-group ${styles.actionButtonsContainer}`}>
                          <button 
                            className={`btn btn-sm btn-outline-primary ${styles.actionButton}`} 
                            onClick={() => navigateToCountry(country.Country_ID)}
                          >
                            <i className="bi bi-pencil-fill"></i>
                          </button>
                          <button 
                            className={`btn btn-sm btn-outline-danger ${styles.actionButton}`} 
                            onClick={() => handleDeleteCountry(country.Country_ID)}
                          >
                            <i className="bi bi-trash-fill"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationManagement;
